import { z } from "zod";
import { CommitShaSchema, IdSchema, TimestampSchema, WpIdSchema } from "./primitives.ts";

/**
 * Lifecycle of a land request.
 * - `pending`: waiting on something outside the agent's control (a verdict, a sequenced session
 *   landing first, a dependency landing, the migration lane). Re-checked automatically.
 * - `approved`: every gate passed; the merge service may merge this commit into main.
 * - `rejected`: refused (failed verdict, unresolved blocking overlap, superseded). Fix and request again.
 * - `merged`: merged into main. The package and session are `landed`.
 * - `conflict`: the merge hit conflicts. Rebase and request again.
 * - `failed`: the merge service could not merge and it was not a conflict (fork gone, push refused
 *   for another reason). The land does not stay `approved`. Fix the cause and request again.
 */
export const LandStatusSchema = z.enum(["pending", "approved", "rejected", "merged", "conflict", "failed"]);
/** See {@link LandStatusSchema}. */
export type LandStatus = z.infer<typeof LandStatusSchema>;

/**
 * A session's request to land a commit into main, and what happened to it.
 * The coordinator moves it through the gates; the merge service reports the merge result.
 */
export const LandRequestSchema = z.object({
  /** Coordinator-assigned id. */
  id: IdSchema,
  /** The requesting session. */
  sessionId: IdSchema,
  /** The session's work package. */
  wpId: WpIdSchema,
  /** The commit to land. */
  commit: CommitShaSchema,
  /** Current state, see {@link LandStatusSchema}. */
  status: LandStatusSchema,
  /** Why it is pending, rejected, conflicted or failed, one reason per line. Empty when approved or merged. */
  reasons: z.array(z.string().min(1)),
  /** The merge commit on main once merged. Null otherwise. */
  mergeCommit: CommitShaSchema.nullable(),
  /** Conflicting paths when status is `conflict`. Empty otherwise. */
  conflicts: z.array(z.string().min(1)),
  /** When it was requested. */
  createdAt: TimestampSchema,
  /** When its status last changed. */
  updatedAt: TimestampSchema,
});
/** See {@link LandRequestSchema}. */
export type LandRequest = z.infer<typeof LandRequestSchema>;

/** One position in the migration lane. */
export const MigrationLaneEntrySchema = z.object({
  /** The migration claim. */
  claimId: IdSchema,
  /** Its session. */
  sessionId: IdSchema,
  /** Its work package. */
  wpId: WpIdSchema,
  /** When it took the lane (holder) or joined the queue. */
  since: TimestampSchema,
});
/** See {@link MigrationLaneEntrySchema}. */
export type MigrationLaneEntry = z.infer<typeof MigrationLaneEntrySchema>;

/**
 * The repo's migration lane. Migration numbering is global, so only one migration claim is active
 * at a time. Others queue in declaration order; when the holder's claim ends (released, expired or
 * landed) the next in line becomes active and is sent an `info` message saying it is their turn.
 */
export const MigrationLaneSchema = z.object({
  /** The active migration claim, or null when the lane is free. */
  holder: MigrationLaneEntrySchema.nullable(),
  /** Queued migration claims, next first. */
  queue: z.array(MigrationLaneEntrySchema),
});
/** See {@link MigrationLaneSchema}. */
export type MigrationLane = z.infer<typeof MigrationLaneSchema>;
